import { leadKey, toPlatform, type Platform } from '../../lib/platform';
import type {
  Creator,
  CreatorAssignmentFields,
  DiscoveryMethod,
  FolderRef,
  Lead,
  LeadAssignee,
  LeadDoneReason,
  LeadEnrichment,
} from '../../api/types';

/**
 * One row of the shared lead table. A folder lead and a registry creator
 * are different records; both are normalised to this so the columns, the
 * bulk bar and the creator sheet are written once.
 */
export interface LeadRow {
  /** Selection key: `l:<lead id>` or `c:<creator id>`. */
  key: string;
  kind: 'lead' | 'creator';
  leadId: number | null;
  creatorId: number;
  username: string;
  platform: Platform;
  /** What lead endpoints take as a "username" (bare for TikTok, `platform:handle` otherwise). */
  handleKey: string;
  enrichment: LeadEnrichment | null;
  /** The original record, for the badges that read registry fields directly. */
  record: Lead | (Creator & CreatorAssignmentFields);
  assignedTo: LeadAssignee | null;
  claimedByName: string | null;
  discoveryMethod: DiscoveryMethod | null;
  discoverySource: string | null;
  topicMatch: number | null;
  addedAt: string;
  doneReason: LeadDoneReason | null;
  lastContactedAt: string | null;
  /** Folders this creator also sits in (folder view) or all its folders (creators view). */
  folders: FolderRef[];
  folderNames: string[];
  notes: string | null;
  suppressedAt: string | null;
}

export function fromLead(l: Lead): LeadRow {
  const platform = toPlatform(l.platform);
  const others = l.otherFolders ?? [];
  return {
    key: `l:${l.id}`,
    kind: 'lead',
    leadId: l.id,
    creatorId: l.creatorId,
    username: l.username,
    platform,
    handleKey: leadKey(platform, l.username),
    enrichment: l.enrichment,
    record: l,
    assignedTo: l.assignedTo ?? null,
    claimedByName: l.claimedByName ?? l.outreachClaimedByName ?? null,
    discoveryMethod: l.discoveryMethod ?? null,
    discoverySource: l.discoverySource ?? null,
    topicMatch: l.topicMatch ?? null,
    addedAt: l.savedAt,
    doneReason: l.doneReason ?? null,
    lastContactedAt: l.lastContactedAt ?? null,
    folders: others,
    folderNames: others.map((f) => f.name),
    notes: l.notes,
    suppressedAt: l.suppressedAt,
  };
}

export function fromCreator(c: Creator & CreatorAssignmentFields): LeadRow {
  const platform = toPlatform(c.platform);
  return {
    key: `c:${c.id}`,
    kind: 'creator',
    leadId: null,
    creatorId: c.id,
    username: c.username,
    platform,
    handleKey: leadKey(platform, c.username),
    enrichment: c.enrichment,
    record: c,
    assignedTo: c.assignedTo ?? null,
    claimedByName: c.claimedByName ?? c.outreachClaimedByName ?? null,
    discoveryMethod: null,
    discoverySource: null,
    topicMatch: null,
    addedAt: c.firstSeenAt,
    doneReason: null,
    lastContactedAt: c.lastContactedAt ?? null,
    folders: [],
    folderNames: c.folderNames ?? [],
    notes: c.notes,
    suppressedAt: c.suppressedAt,
  };
}

/** "Ava Chen", "Anyone on SOCIAL_MEDIA", or null when unassigned. */
export function assigneeLabel(a: LeadAssignee | null | undefined): string | null {
  if (!a) return null;
  if (a.inviterName) return a.inviterName;
  if (a.team) return `Anyone on ${a.team}`;
  return null;
}

export function formatCount(n: unknown): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
